import { ReactElement } from 'react';
import { createPortal } from 'react-dom';

import {
  DragDropContext,
  Draggable,
  DraggableStateSnapshot,
  Droppable,
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import { LocalColumnConfig } from 'app/modules/localTableSettings/models';
import {
  U21Checkbox,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { IconGripVertical } from '@u21/tabler-icons';

interface Props {
  columns: LocalColumnConfig[];
  onChange: (next: LocalColumnConfig[]) => void;
}

// `react-beautiful-dnd` positions the dragged node with `position: fixed`, but
// coordinates are relative to the nearest transformed/overflow-clipped ancestor.
// Inside the sidebar's scroll container this causes the dragged item to clip.
// Portal it to <body> while dragging to escape the clip.
const dragPortal = document.createElement('div');
document.body.appendChild(dragPortal);

const renderIntoPortalIfDragging = (
  snapshot: DraggableStateSnapshot,
  child: ReactElement,
): ReactElement => {
  if (snapshot.isDragging) {
    return createPortal(child, dragPortal);
  }
  return child;
};

export const ColumnPickerList = ({ columns, onChange }: Props) => {
  return (
    <DragDropContext
      onDragEnd={(result) => {
        const { source, destination } = result;
        if (!destination || destination.index === source.index) {
          return;
        }
        const next = [...columns];
        const [removed] = next.splice(source.index, 1);
        next.splice(destination.index, 0, removed);
        onChange(next);
      }}
    >
      <Droppable droppableId="columns">
        {(provided) => (
          <StyledList ref={provided.innerRef} {...provided.droppableProps}>
            {columns.map((column, index) => (
              <Draggable
                key={column.key}
                draggableId={column.key}
                index={index}
              >
                {(draggableProvided, snapshot) =>
                  renderIntoPortalIfDragging(
                    snapshot,
                    <StyledItem
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                    >
                      <StyledSpacer
                        horizontal
                        align={column.description ? 'start' : undefined}
                      >
                        <StyledIconContainer>
                          <IconGripVertical />
                        </StyledIconContainer>
                        <U21Spacer spacing={0.5}>
                          <StyledLabel htmlFor={column.key}>
                            {column.label}
                          </StyledLabel>
                          {column.description && (
                            <U21Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {column.description}
                            </U21Typography>
                          )}
                        </U21Spacer>
                      </StyledSpacer>
                      <U21Checkbox
                        checked={!column.hidden}
                        id={column.key}
                        name={column.key}
                        onChange={(checked: boolean) => {
                          const shouldHide = !checked;
                          onChange(
                            columns.map((c) =>
                              c.key === column.key
                                ? { ...c, hidden: shouldHide }
                                : c,
                            ),
                          );
                        }}
                      />
                    </StyledItem>,
                  )
                }
              </Draggable>
            ))}
            {provided.placeholder}
          </StyledList>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const StyledList = styled.ul`
  list-style: none;
`;

const StyledItem = styled.li`
  padding: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  display: flex;
  width: 100%;
  align-items: center;
`;

const StyledSpacer = styled(U21Spacer)`
  flex-grow: 1;
`;

const StyledIconContainer = styled.div`
  display: flex;
`;

const StyledLabel = styled.label`
  flex: 1;
  overflow-wrap: anywhere;
`;
